import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { Redirect, useParams } from "react-router-dom";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Typography
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import ptBrLocale from "date-fns/locale/pt-BR";
import ReactDataGrid from "@inovua/reactdatagrid-enterprise";
import "@inovua/reactdatagrid-community/index.css";
import styled from "styled-components";
import Group from "../../waybee-ui/Group";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import BackButton from "../../components/BackButton";
import GridFullscreen from "../../components/GridFullscreen";
import JobInProgressLoading from "../../components/JobInProgressLoading";
import useJobQueueWaiting from "../../utils/useJobQueueWaiting";
import Button from "../../waybee-ui/Button";
import SimpleDialog from "../../waybee-ui/Dialog/SimpleDialog";
import { getDataGridI18n } from "../../utils/getDataGridI18n";
import { clearLeaveGuard, setLeaveGuard } from "../../utils/leaveGuard";
import { AbilityContext } from "../../acl/can";
import withClassAbility from "../../acl/withClassAbility";
import ClassService from "../../services/ClassService";
import ClassPlanService from "../../services/ClassPlanService";
import {
  buildGridRows,
  currentMonth,
  estimateGridHeight,
  groupLessonsByDay,
  shiftMonth
} from "./utils/classPlanGrid";
import {
  buildEditableRows,
  buildLessonOwners,
  buildSavePayload,
  createEdits,
  hasPendingChanges,
  needsLessons,
  toggleLesson,
  toggleRowDeletion,
  updateDraftText,
  updateRowText,
  validateEdits
} from "./utils/classPlanEditing";
import { buildClassPlanGrid } from "./components/buildClassPlanGrid";

const CLASS_PLAN_JOB = "save_class_plan";
const CLASS_PLAN_JOB_PERIOD = "Plano de aula";
const MONTH_FORMAT = "YYYY-MM";
const READ_ONLY_EDITS = { ...createEdits(), newRows: [] };

const PlanCard = styled(Group)`
  padding: 30px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding-bottom: 16px;
`;

const ToolbarFilters = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MissingLessonsNotice = styled(Typography)`
  && {
    flex-basis: 100%;
    margin: 0;
    padding: 4px 10px;
    background-color: #fff3e0;
    border-left: 4px solid #ff9800;
    font-size: 14px;
  }
`;

const DisciplineSelect = styled(SearchableSelect)`
  width: 280px;
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MonthPicker = styled(DatePicker)`
  width: 180px;
`;

const EMPTY_GRID = { lessons: [], contents: [] };

const ClassPlan = () => {
  const { id: classId } = useParams();
  const alert = useAlert();
  const { t, i18n } = useTranslation();
  const ability = useContext(AbilityContext);
  const canSave = ability.can("save", "classPlan");

  const [disciplines, setDisciplines] = useState([]);
  const [idTurmaDisc, setIdTurmaDisc] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [grid, setGrid] = useState(EMPTY_GRID);
  const [loadingDisciplines, setLoadingDisciplines] = useState(true);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [edits, setEdits] = useState(createEdits);
  const [saving, setSaving] = useState(false);
  const [pendingFilter, setPendingFilter] = useState(null);

  const showError = useCallback(
    message => alert.show(message, { title: t("common.actions.error") }),
    [alert, t]
  );

  const matchesJob = useCallback(
    job =>
      job.name === CLASS_PLAN_JOB &&
      String(job.classId) === String(classId) &&
      String(job.course) === String(idTurmaDisc),
    [classId, idTurmaDisc]
  );
  const reload = useCallback(() => setReloadKey(key => key + 1), []);
  const { waiting, startWaiting } = useJobQueueWaiting(matchesJob, reload);

  useEffect(() => {
    let active = true;
    ClassService.getDisciplines(classId)
      .then(result => {
        if (!active) return;
        const list = Array.isArray(result) ? result : [];
        setDisciplines(list);
        if (list.length) setIdTurmaDisc(String(list[0].idTurmaDisc));
      })
      .catch(() => active && showError(t("common.messages.errorFetchDisciplines")))
      .finally(() => active && setLoadingDisciplines(false));
    return () => {
      active = false;
    };
  }, [classId, showError, t]);

  useEffect(() => {
    if (!idTurmaDisc) return undefined;
    let active = true;
    setLoadingGrid(true);
    setEdits(createEdits());
    ClassPlanService.getClassPlanGrid(classId, idTurmaDisc, month)
      .then(result => active && setGrid(result || EMPTY_GRID))
      .catch(() => {
        if (!active) return;
        setGrid(EMPTY_GRID);
        showError(t("classPlan.loadError"));
      })
      .finally(() => active && setLoadingGrid(false));
    return () => {
      active = false;
    };
  }, [classId, idTurmaDisc, month, reloadKey, showError, t]);

  const locale = i18n.language === "en" ? "en" : "pt-br";

  const gridRows = useMemo(() => buildGridRows(grid.contents), [
    grid.contents
  ]);

  const dataSource = useMemo(
    () => buildEditableRows(gridRows, canSave ? edits : READ_ONLY_EDITS),
    [gridRows, edits, canSave]
  );

  const owners = useMemo(() => buildLessonOwners(dataSource), [dataSource]);
  const pending = canSave && hasPendingChanges(gridRows, edits);

  useEffect(() => {
    setLeaveGuard(() => pending);
    return clearLeaveGuard;
  }, [pending]);
  const hasRowsWithoutLessons = dataSource.some(needsLessons);
  const filtersDisabled = waiting || saving;

  const handleToggleLesson = useCallback(
    (row, lessonId) =>
      setEdits(current => {
        const rows = buildEditableRows(gridRows, current);
        const currentRow = rows.find(item => item.id === row.id);
        if (!currentRow) return current;
        return toggleLesson(current, currentRow, lessonId, buildLessonOwners(rows));
      }),
    [gridRows]
  );

  const handleEditComplete = useCallback(({ value, data }) => {
    setEdits(current =>
      data.isNew
        ? updateDraftText(current, data.id, value)
        : updateRowText(current, data, value)
    );
  }, []);

  const handleToggleDelete = useCallback(
    rowId =>
      setEdits(current => {
        const rows = buildEditableRows(gridRows, current);
        const row = rows.find(item => item.id === rowId);
        if (!row) return current;
        return toggleRowDeletion(current, row, buildLessonOwners(rows));
      }),
    [gridRows]
  );

  const { columns, groups, rowStyle } = useMemo(
    () =>
      buildClassPlanGrid(groupLessonsByDay(month, grid.lessons), {
        contentLabel: t("classPlan.content"),
        noLessonLabel: t("classPlan.noLesson"),
        draftPlaceholder: t("classPlan.draftPlaceholder"),
        deleteLabel: t("classPlan.deleteRow"),
        undoLabel: t("classPlan.undoDeleteRow"),
        missingLessonsLabel: t("classPlan.missingLessonsHint"),
        locale,
        editable: canSave && !saving,
        owners,
        onToggleLesson: handleToggleLesson,
        onToggleDelete: handleToggleDelete
      }),
    [
      month,
      grid.lessons,
      locale,
      t,
      canSave,
      saving,
      owners,
      handleToggleLesson,
      handleToggleDelete
    ]
  );

  const applyFilter = filter => {
    if (filter.idTurmaDisc !== undefined) setIdTurmaDisc(filter.idTurmaDisc);
    if (filter.month !== undefined) setMonth(filter.month);
  };

  const changeFilter = filter => {
    if (pending) {
      setPendingFilter(filter);
      return;
    }
    applyFilter(filter);
  };

  const discardAndApplyFilter = () => {
    applyFilter(pendingFilter);
    setPendingFilter(null);
  };

  const selectedDiscipline =
    disciplines.find(d => String(d.idTurmaDisc) === idTurmaDisc) || null;

  const handleSave = async () => {
    const validationError = validateEdits(gridRows, edits);
    if (validationError) {
      showError(t(`classPlan.validation.${validationError}`));
      return;
    }

    setSaving(true);
    try {
      await ClassPlanService.saveClassPlanJob({
        classId,
        filter: {
          course: {
            id: idTurmaDisc,
            name: selectedDiscipline && selectedDiscipline.name
          },
          period: CLASS_PLAN_JOB_PERIOD
        },
        ...buildSavePayload(gridRows, edits)
      });
      alert.show(t("common.messages.processingRequest"), {
        title: t("classPlan.save.sent")
      });
      setEdits(createEdits());
      startWaiting();
    } catch (error) {
      showError(
        error?.response?.data?.error?.message || t("classPlan.save.error")
      );
    } finally {
      setSaving(false);
    }
  };

  const renderFilters = () => (
    <ToolbarFilters>
      <DisciplineSelect
        label={t("common.labels.discipline")}
        options={disciplines}
        value={selectedDiscipline}
        onChange={discipline =>
          changeFilter({
            idTurmaDisc: discipline ? String(discipline.idTurmaDisc) : ""
          })}
        disableClearable
        disabled={filtersDisabled}
        getOptionLabel={o => o.name || ""}
        getOptionSelected={(a, b) =>
          String(a.idTurmaDisc) === String(b.idTurmaDisc)}
        noOptionsText={t("common.messages.noDiscipline")}
        TextFieldProps={{ variant: "outlined", size: "small" }}
      />
      <MonthNavigation>
        <IconButton
          size="small"
          aria-label={t("classPlan.previousMonth")}
          title={t("classPlan.previousMonth")}
          disabled={filtersDisabled}
          onClick={() => changeFilter({ month: shiftMonth(month, -1) })}
        >
          <ChevronLeftIcon />
        </IconButton>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBrLocale}>
          <MonthPicker
            variant="inline"
            inputVariant="outlined"
            size="small"
            views={["year", "month"]}
            openTo="month"
            format="MMMM yyyy"
            label={t("classPlan.month")}
            disabled={filtersDisabled}
            value={moment(month, MONTH_FORMAT).toDate()}
            onChange={date =>
              changeFilter({ month: moment(date).format(MONTH_FORMAT) })}
            autoOk
          />
        </MuiPickersUtilsProvider>
        <IconButton
          size="small"
          aria-label={t("classPlan.nextMonth")}
          title={t("classPlan.nextMonth")}
          disabled={filtersDisabled}
          onClick={() => changeFilter({ month: shiftMonth(month, 1) })}
        >
          <ChevronRightIcon />
        </IconButton>
      </MonthNavigation>
    </ToolbarFilters>
  );

  const renderToolbar = trigger => (
    <Toolbar>
      {renderFilters()}
      <ToolbarActions>
        {canSave && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!pending || saving}
            onClick={handleSave}
          >
            {saving ? t("common.actions.saving") : t("common.actions.save")}
          </Button>
        )}
        {trigger}
      </ToolbarActions>
      {hasRowsWithoutLessons && (
        <MissingLessonsNotice variant="body2">
          {t("classPlan.missingLessonsNotice")}
        </MissingLessonsNotice>
      )}
    </Toolbar>
  );

  const renderGrid = isFullscreen => {
    if (waiting) {
      return (
        <JobInProgressLoading
          message={t("classPlan.jobInProgressCourse", {
            course: (selectedDiscipline && selectedDiscipline.name) || ""
          })}
        />
      );
    }
    if (loadingGrid) {
      return (
        <Box p={6} textAlign="center">
          <CircularProgress />
        </Box>
      );
    }
    return (
      <ReactDataGrid
        idProperty="id"
        columns={columns}
        groups={groups}
        dataSource={dataSource}
        rowStyle={rowStyle}
        rowHeight={null}
        minRowHeight={36}
        style={
          isFullscreen
            ? { height: "100%", minHeight: 0, flex: 1 }
            : { height: estimateGridHeight(dataSource.length) }
        }
        licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE_KEY}
        i18n={getDataGridI18n(t)}
        emptyText={t("classPlan.empty")}
        reorderColumns={false}
        sortable={false}
        showColumnMenuToolOnHover={false}
        showCellBorders
        showZebraRows={false}
        editable={canSave && !saving}
        editStartEvent="click"
        onEditComplete={handleEditComplete}
      />
    );
  };

  return (
    <Grid container spacing={2}>
      <BackButton to={`/classes/${classId}`} />
      <Grid item xs={12}>
        <PlanCard>
          {loadingDisciplines ? (
            <Box p={4} textAlign="center">
              <CircularProgress />
            </Box>
          ) : (
            <GridFullscreen
              minHeight={estimateGridHeight(dataSource.length)}
              renderToolbar={renderToolbar}
            >
              {({ isFullscreen }) => renderGrid(isFullscreen)}
            </GridFullscreen>
          )}
        </PlanCard>
      </Grid>
      <SimpleDialog
        open={!!pendingFilter}
        onClose={() => setPendingFilter(null)}
        title={t("classPlan.unsaved.title")}
        message={t("classPlan.unsaved.message")}
        actions={(
          <>
            <Button color="default" onClick={() => setPendingFilter(null)}>
              {t("common.actions.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={discardAndApplyFilter}
            >
              {t("classPlan.unsaved.discard")}
            </Button>
          </>
        )}
      />
    </Grid>
  );
};

const ClassPlanAccess = () => {
  const { id: classId } = useParams();
  const ability = useContext(AbilityContext);

  if (!ability.can("use module", "classPlan")) {
    return <Redirect to={`/classes/${classId}`} />;
  }

  return <ClassPlan />;
};

export default withClassAbility(ClassPlanAccess);
